/**
 * Credit / Due (Udhar) — bill-wise tracking.
 *
 * Firestore collections:
 * - `creditBills` — one doc per credit bill (linked to outward invoice or manual entry)
 * - `creditPayments` — payment receipts with allocations to bills
 *
 * Security: add Firestore rules to restrict read/write to authenticated users as needed.
 */
import { db } from "../firebase";
import { normalizeBranchValue } from "../config/appBranches";
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

export const COLLECTION_BILLS = "creditBills";
export const COLLECTION_PAYMENTS = "creditPayments";
export const COLLECTION_AUDIT_NOTES = "creditCustomerAuditNotes";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function firstDefinedString(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

function firstNumber(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

/**
 * Map CSV / spreadsheet / import field names onto the camelCase fields the app uses.
 * In-memory only; does not rewrite Firestore.
 */
export function normalizeCreditBillRow(b) {
  if (!b || typeof b !== "object") return b;
  const out = { ...b };
  const cid = firstDefinedString(out.customerId, out.customerid);
  if (cid) out.customerId = cid;
  out.balanceAmount = firstNumber(out.balanceAmount, out.balanceamount);
  out.paidAmount = firstNumber(out.paidAmount, out.paidamount);
  out.totalAmount = firstNumber(out.totalAmount, out.totalamount);
  const nm = firstDefinedString(out.customerName, out.customername);
  if (nm) out.customerName = nm;
  const vn = firstDefinedString(out.voucherNo, out.voucherno);
  if (vn) out.voucherNo = vn;
  const inv = firstDefinedString(out.invoiceDate, out.invoicedate);
  if (inv) out.invoiceDate = inv;
  const due = firstDefinedString(out.dueDate, out.duedate);
  if (due) out.dueDate = due;
  const co = firstDefinedString(out.careOf, out.careof);
  if (co) out.careOf = co;
  const created = out.createdAt ?? out.createdat;
  if (created !== undefined && created !== null) out.createdAt = created;
  return out;
}

/** Canonical branch on a credit bill (missing → Main). */
export function creditBillBranch(b) {
  return normalizeBranchValue(b?.branch);
}

/**
 * Branch for a payment: stored `branch`, else first allocated bill's branch, else Main.
 * @param {object} p — payment doc
 * @param {Map<string, object>} [billById] — optional map bill id → bill row
 */
export function creditPaymentBranch(p, billById) {
  if (p?.branch != null && String(p.branch).trim() !== "") {
    return normalizeBranchValue(p.branch);
  }
  const allocs = Array.isArray(p?.allocations) ? p.allocations : [];
  for (const a of allocs) {
    const bid = String(a?.billId || "").trim();
    if (!bid || !billById) continue;
    const b = billById.get(bid);
    if (b) return creditBillBranch(b);
  }
  return "Main";
}

/** @typedef {'pending'|'partial'|'paid'} BillStatus */

export function deriveStatus(total, paid, balance) {
  const b = round2(balance);
  if (b <= 0) return "paid";
  if (round2(paid) > 0) return "partial";
  return "pending";
}

/**
 * After a successful outward invoice save with CREDIT — creates a credit bill row.
 */
export async function createCreditBillFromInvoice({
  invoiceId,
  voucherNo,
  customerId,
  customerName,
  phone,
  totalAmount,
  dueDate,
  /** ISO date string (YYYY-MM-DD) or parseable date — used for aging & Credit days (Σ); must match billing screen invoice date */
  invoiceDate,
  /** @type {string} [branch] — Main | Branch 2 | Branch 3 */
  branch,
}) {
  const total = round2(totalAmount);
  if (!customerId || total <= 0) {
    console.warn("createCreditBillFromInvoice: missing customer or amount");
    return null;
  }
  const invStr =
    typeof invoiceDate === "string" && invoiceDate.trim()
      ? invoiceDate.trim()
      : invoiceDate instanceof Date && !Number.isNaN(invoiceDate.getTime())
        ? invoiceDate.toISOString().slice(0, 10)
        : null;
  const payload = {
    invoiceId: invoiceId || null,
    voucherNo: voucherNo || "",
    customerId,
    customerName: customerName || "",
    phone: phone || "",
    totalAmount: total,
    paidAmount: 0,
    balanceAmount: total,
    status: "pending",
    createdAt: serverTimestamp(),
    dueDate: dueDate || null,
    invoiceDate: invStr,
    source: "invoice",
    manual: false,
    branch: normalizeBranchValue(branch),
  };
  const ref = await addDoc(collection(db, COLLECTION_BILLS), payload);
  return { id: ref.id, ...payload };
}

/**
 * Parse DD-MM-YYYY, DD-MM-YY, DD/MM/… (common manual Udhar / India entry). Two-digit years → 20YY.
 * @param {string} s
 * @returns {Date | null}
 */
function parseIndianDmyDateString(s) {
  const t = String(s || "").trim();
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  let year = parseInt(m[3], 10);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/**
 * Manual Udhar entry (no invoice / stock).
 * @param {{ invoiceNo: string }} — user-entered bill / invoice number (stored as voucherNo).
 */
function parseFirestoreOrIsoDate(val) {
  if (!val) return null;
  if (val?.toDate) {
    const d = val.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "string" && val) {
    const s = val.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const d = new Date(`${s.slice(0, 10)}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const dmy = parseIndianDmyDateString(s);
    if (dmy) return dmy;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Overdue checks: use explicit due date when set; otherwise invoice date + 30 days for manual bills.
 */
export function effectiveDueDateForOverdue(b) {
  const due = parseFirestoreOrIsoDate(b.dueDate);
  if (due) {
    due.setHours(0, 0, 0, 0);
    return due;
  }
  const inv = parseFirestoreOrIsoDate(b.invoiceDate);
  if (!inv) return null;
  const d = new Date(inv);
  d.setDate(d.getDate() + 30);
  d.setHours(0, 0, 0, 0);
  return d;
}

export async function createManualCreditBill({
  customerId,
  customerName,
  phone,
  totalAmount,
  invoiceDate,
  note,
  invoiceNo,
  careOf,
  /** @type {string} [branch] */
  branch,
}) {
  const total = round2(totalAmount);
  if (!customerId || total <= 0) throw new Error("Customer and amount required");
  const voucherNo = String(invoiceNo || "").trim();
  if (!voucherNo) throw new Error("Invoice number is required");
  const inv = String(invoiceDate || "").trim();
  if (!inv) throw new Error("Invoice date is required");
  const co = String(careOf || "").trim();
  const payload = {
    invoiceId: null,
    voucherNo,
    customerId,
    customerName: customerName || "",
    phone: phone || "",
    totalAmount: total,
    paidAmount: 0,
    balanceAmount: total,
    status: "pending",
    createdAt: serverTimestamp(),
    invoiceDate: inv,
    dueDate: null,
    source: "manual",
    manual: true,
    note: note || "",
    careOf: co || null,
    branch: normalizeBranchValue(branch),
  };
  const ref = await addDoc(collection(db, COLLECTION_BILLS), payload);
  return { id: ref.id, ...payload };
}

export async function fetchAllCreditBills() {
  const snap = await getDocs(collection(db, COLLECTION_BILLS));
  return snap.docs.map((d) => normalizeCreditBillRow({ id: d.id, ...d.data() }));
}

export async function fetchCreditBillById(billId) {
  if (!billId) return null;
  const ref = doc(db, COLLECTION_BILLS, billId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return normalizeCreditBillRow({ id: snap.id, ...snap.data() });
}

/**
 * Update a credit bill. Manual entries can edit bill no, dates, amount (≥ paid); invoice-linked rows only metadata (due, note, c/o, display name/phone).
 * @param {string} billId
 * @param {{
 *   voucherNo?: string,
 *   invoiceDate?: string,
 *   dueDate?: string | null,
 *   totalAmount?: number,
 *   note?: string,
 *   careOf?: string | null,
 *   customerName?: string,
 *   phone?: string,
 * }} patch
 */
export async function updateCreditBill(billId, patch) {
  const id = String(billId || "").trim();
  if (!id) throw new Error("Bill id required");
  const ref = doc(db, COLLECTION_BILLS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Bill not found");
  const data = normalizeCreditBillRow({ id: snap.id, ...snap.data() });
  const manual = data.manual === true;
  const paid = round2(data.paidAmount);

  const updates = { updatedAt: serverTimestamp() };

  if (patch.customerName !== undefined) {
    updates.customerName = String(patch.customerName || "").trim();
  }
  if (patch.phone !== undefined) {
    updates.phone = String(patch.phone || "").trim();
  }
  if (patch.note !== undefined) {
    updates.note = String(patch.note || "");
  }
  if (patch.careOf !== undefined) {
    const co = String(patch.careOf || "").trim();
    updates.careOf = co || null;
  }
  if (patch.dueDate !== undefined) {
    const raw = patch.dueDate;
    if (raw === null || raw === "") updates.dueDate = null;
    else updates.dueDate = String(raw).trim().slice(0, 10) || null;
  }

  if (manual) {
    if (patch.voucherNo !== undefined) {
      const v = String(patch.voucherNo || "").trim();
      if (!v) throw new Error("Invoice / bill number is required");
      updates.voucherNo = v;
    }
    if (patch.invoiceDate !== undefined) {
      const inv = String(patch.invoiceDate || "").trim();
      if (!inv) throw new Error("Invoice date is required");
      updates.invoiceDate = inv;
    }
    if (patch.totalAmount !== undefined) {
      const total = round2(patch.totalAmount);
      if (total <= 0) throw new Error("Amount must be positive");
      if (total + 0.001 < paid) throw new Error("Total cannot be less than amount already received");
      updates.totalAmount = total;
      const newBal = round2(total - paid);
      updates.balanceAmount = newBal;
      updates.status = deriveStatus(total, paid, newBal);
    }
  } else {
    if (
      patch.voucherNo !== undefined ||
      patch.invoiceDate !== undefined ||
      patch.totalAmount !== undefined
    ) {
      throw new Error("Invoice-linked bills: only due date, note, care of, and display name/phone can be edited here");
    }
  }

  await updateDoc(ref, updates);
  const after = await getDoc(ref);
  return normalizeCreditBillRow({ id: after.id, ...after.data() });
}

/** Sort key for bill `createdAt` (Timestamp, seconds map, or ISO string). */
function billCreatedMillisForSort(b) {
  const c = b?.createdAt ?? b?.createdat;
  if (c?.toDate) return c.toDate().getTime();
  if (typeof c?.seconds === "number") return c.seconds * 1000;
  if (typeof c === "string" && c.trim()) {
    const d = new Date(c.trim());
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
}

/** Newest-first sort key for payment rows (before `paymentTimestamp` is defined in this file). */
function paymentSortMillis(p) {
  if (!p) return 0;
  const raw = p?.paymentDate;
  if (typeof raw === "string") {
    const t = raw.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      const [y, mo, d] = t.split("-").map(Number);
      return new Date(y, mo - 1, d, 12, 0, 0, 0).getTime();
    }
  }
  return billCreatedMillisForSort(p);
}

async function fetchBillsForCustomerSingleField(field, cid) {
  const col = collection(db, COLLECTION_BILLS);
  try {
    const qy = query(col, where(field, "==", cid), orderBy("createdAt", "desc"));
    const snap = await getDocs(qy);
    return snap.docs.map((d) => normalizeCreditBillRow({ id: d.id, ...d.data() }));
  } catch {
    try {
      const snap = await getDocs(query(col, where(field, "==", cid)));
      const rows = snap.docs.map((d) => normalizeCreditBillRow({ id: d.id, ...d.data() }));
      rows.sort((a, b) => billCreatedMillisForSort(b) - billCreatedMillisForSort(a));
      return rows;
    } catch {
      return [];
    }
  }
}

/**
 * All credit bills for a party. Queries both `customerId` and legacy `customerid` (import / CSV) and merges by doc id.
 */
export async function fetchBillsForCustomer(customerId) {
  const cid = String(customerId || "").trim();
  if (!cid) return [];

  const [camelRows, lowerRows] = await Promise.all([
    fetchBillsForCustomerSingleField("customerId", cid),
    fetchBillsForCustomerSingleField("customerid", cid),
  ]);
  const byId = new Map();
  for (const r of [...camelRows, ...lowerRows]) {
    if (r?.id) byId.set(r.id, r);
  }
  const rows = Array.from(byId.values());
  rows.sort((a, b) => billCreatedMillisForSort(b) - billCreatedMillisForSort(a));
  return rows;
}

export async function fetchPendingBillsOldestFirst(customerId) {
  const bills = await fetchBillsForCustomer(customerId);
  const pending = bills.filter((b) => round2(b.balanceAmount) > 0);
  pending.sort(compareBillsFifoOldestFirst);
  return pending;
}

/**
 * Start date for “how long has this bill been open” (invoice date, else bill createdAt).
 */
export function creditAgeStartDate(b) {
  if (b?.invoiceDate) {
    const d = parseFirestoreOrIsoDate(b.invoiceDate);
    if (d) return d;
  }
  const c = b?.createdAt ?? b?.createdat;
  if (c?.toDate) return c.toDate();
  if (typeof c?.seconds === "number") return new Date(c.seconds * 1000);
  if (typeof c === "string" && c.trim()) {
    const d = new Date(c.trim());
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/** Milliseconds for FIFO: invoice / bill date first (old due), then Firestore createdAt. */
function billFifoSortMillis(b) {
  const start = creditAgeStartDate(b);
  if (start && !Number.isNaN(start.getTime())) return start.getTime();
  return billCreatedMillisForSort(b);
}

/** Oldest invoice date first; ties broken by record createdAt. */
function compareBillsFifoOldestFirst(a, b) {
  const diff = billFifoSortMillis(a) - billFifoSortMillis(b);
  if (diff !== 0) return diff;
  return billCreatedMillisForSort(a) - billCreatedMillisForSort(b);
}

/** Whole days from age start to today (0 if unknown or future-dated start). */
export function daysOutstandingForBill(b) {
  const start = creditAgeStartDate(b);
  if (!start) return 0;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const diff = Math.floor((t - s) / 86400000);
  return Math.max(0, diff);
}

/**
 * Aggregate per customer: total due, overdue flag, sum of days-outstanding across open bills.
 */
export function summarizeCustomersFromBills(billRows) {
  const map = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (const b of billRows) {
    const bal = round2(b.balanceAmount);
    if (bal <= 0) continue;
    const cid = b.customerId;
    if (!cid) continue;
    if (!map.has(cid)) {
      map.set(cid, {
        customerId: cid,
        customerName: b.customerName || "",
        phone: b.phone || "",
        totalDue: 0,
        overdueAmount: 0,
        hasOverdue: false,
        totalCreditDays: 0,
      });
    }
    const row = map.get(cid);
    row.totalDue += bal;
    row.totalCreditDays += daysOutstandingForBill(b);
    const dueD = effectiveDueDateForOverdue(b);
    if (dueD && dueD < today) {
      row.overdueAmount += bal;
      row.hasOverdue = true;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.totalDue - a.totalDue);
}

export async function fetchAllPayments() {
  const snap = await getDocs(collection(db, COLLECTION_PAYMENTS));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function fetchPaymentsForCustomerSingleField(field, cid) {
  const col = collection(db, COLLECTION_PAYMENTS);
  try {
    const qy = query(col, where(field, "==", cid), orderBy("createdAt", "desc"));
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    try {
      const snap = await getDocs(query(col, where(field, "==", cid)));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => paymentSortMillis(b) - paymentSortMillis(a));
      return rows;
    } catch {
      return [];
    }
  }
}

/** Payments for a party — queries `customerId` and legacy `customerid`, merged by doc id. */
export async function fetchPaymentsForCustomer(customerId) {
  const cid = String(customerId || "").trim();
  if (!cid) return [];

  const [camelRows, lowerRows] = await Promise.all([
    fetchPaymentsForCustomerSingleField("customerId", cid),
    fetchPaymentsForCustomerSingleField("customerid", cid),
  ]);
  const byId = new Map();
  for (const r of [...camelRows, ...lowerRows]) {
    if (r?.id) byId.set(r.id, r);
  }
  const rows = Array.from(byId.values());
  rows.sort((a, b) => paymentSortMillis(b) - paymentSortMillis(a));
  return rows;
}

/** Local calendar YYYY-MM-DD for the given `Date` (browser timezone). */
function localYMD(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function createdAtToDate(p) {
  const c = p?.createdAt;
  if (c?.toDate) return c.toDate();
  if (typeof c?.seconds === "number") return new Date(c.seconds * 1000);
  return null;
}

/**
 * Receipt / value date for sorting, display, and “today’s collection”.
 * Uses `paymentDate` (YYYY-MM-DD) when set; otherwise Firestore `createdAt`.
 */
export function paymentTimestamp(p) {
  const raw = p?.paymentDate;
  if (typeof raw === "string") {
    const t = raw.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      const [y, mo, d] = t.split("-").map(Number);
      return new Date(y, mo - 1, d, 12, 0, 0, 0);
    }
  }
  const d = createdAtToDate(p);
  return d && !Number.isNaN(d.getTime()) ? d : new Date(0);
}

/** Default value for date inputs when editing a payment (explicit `paymentDate` or local calendar day of `createdAt`). */
export function paymentDateInputValue(p) {
  const raw = p?.paymentDate;
  if (typeof raw === "string") {
    const t = raw.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  }
  const d = createdAtToDate(p);
  if (!d || Number.isNaN(d.getTime())) return "";
  return localYMD(d);
}

/** Newest first */
export async function fetchCreditPaymentsSorted() {
  const rows = await fetchAllPayments();
  rows.sort((a, b) => paymentTimestamp(b) - paymentTimestamp(a));
  return rows;
}

function paymentTs(p) {
  return paymentTimestamp(p);
}

export function todayCollectionAmount(payments, dayISO) {
  const day = dayISO || new Date().toISOString().slice(0, 10);
  let sum = 0;
  for (const p of payments) {
    const d = paymentTs(p);
    if (d.toISOString().slice(0, 10) === day) sum += round2(p.amount);
  }
  return round2(sum);
}

export function totalsOverdue(billRows) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let overdue = 0;
  for (const b of billRows) {
    const bal = round2(b.balanceAmount);
    if (bal <= 0) continue;
    const dueD = effectiveDueDateForOverdue(b);
    if (dueD && dueD < today) overdue += bal;
  }
  return round2(overdue);
}

export function totalDueForBillRows(billRows) {
  return round2(billRows.reduce((s, b) => s + Math.max(0, round2(b.balanceAmount)), 0));
}

/**
 * Allocate payment FIFO across pending bills (oldest invoice / bill date first).
 * @returns {{ allocations: { billId: string, amount: number }[], remaining: number }}
 */
export function allocateFIFO(pendingBillsOldestFirst, amount) {
  let remaining = round2(amount);
  const allocations = [];
  for (const b of pendingBillsOldestFirst) {
    if (remaining <= 0) break;
    const bal = round2(b.balanceAmount);
    if (bal <= 0) continue;
    const pay = Math.min(remaining, bal);
    allocations.push({ billId: b.id, amount: pay });
    remaining = round2(remaining - pay);
  }
  return { allocations, remaining };
}

/**
 * Merge multiple allocation lines for the same bill (manual + FIFO on one bill).
 */
export function mergeAllocationsByBillId(allocations) {
  const map = new Map();
  for (const a of allocations) {
    const id = a.billId;
    const amt = round2(a.amount);
    if (amt <= 0 || !id) continue;
    map.set(id, round2((map.get(id) || 0) + amt));
  }
  return Array.from(map.entries()).map(([billId, amount]) => ({ billId, amount }));
}

/**
 * FIFO on remaining balance after per-bill manual amounts are reserved (oldest bills first).
 * @param {Record<string, string|number>} manualByBillId — optional pay-now amount per bill id
 */
export function allocateFIFOAfterManual(pendingBillsOldestFirst, manualByBillId, fifoAmount) {
  let remaining = round2(Number(fifoAmount) || 0);
  if (remaining <= 0) return { allocations: [], remainingUnallocated: 0 };
  const allocations = [];
  for (const b of pendingBillsOldestFirst) {
    if (remaining <= 0) break;
    const bal = round2(b.balanceAmount);
    if (bal <= 0) continue;
    const manual = round2(parseFloat(manualByBillId[b.id] ?? 0) || 0);
    const room = Math.max(0, round2(bal - manual));
    if (room <= 0) continue;
    const pay = Math.min(remaining, room);
    allocations.push({ billId: b.id, amount: pay });
    remaining = round2(remaining - pay);
  }
  return { allocations, remainingUnallocated: remaining };
}

/**
 * One payment: per-bill manual amounts first, then FIFO pool applied to what's left on each bill (oldest first).
 * Both can be used in the same receipt.
 *
 * @param {string | null} [branchFilter] — When set (e.g. `Main`, `Branch 2`), only bills for that outlet are paid
 *   and the receipt is tagged to that branch. Must match the Credit / Due UI branch or FIFO can hit other shops.
 */
export async function applyPaymentCombined(
  customerId,
  manualByBillId,
  fifoAmount,
  note,
  payMode = "cash",
  branchFilter = null
) {
  let pending = await fetchPendingBillsOldestFirst(customerId);
  if (branchFilter != null && String(branchFilter).trim() !== "") {
    const br = normalizeBranchValue(branchFilter);
    pending = pending.filter((b) => creditBillBranch(b) === br);
  }
  if (!pending.length) throw new Error("No pending bills to pay for this branch");

  const manualAlloc = [];
  for (const b of pending) {
    const v = round2(parseFloat(manualByBillId[b.id] ?? 0) || 0);
    if (v <= 0) continue;
    const bal = round2(b.balanceAmount);
    if (v > bal + 0.001) throw new Error(`Amount exceeds balance for ${b.voucherNo || b.id}`);
    manualAlloc.push({ billId: b.id, amount: v });
  }

  const fifoRaw = round2(parseFloat(fifoAmount) || 0);
  const { allocations: fifoAlloc } = allocateFIFOAfterManual(pending, manualByBillId, fifoRaw);

  const merged = mergeAllocationsByBillId([...manualAlloc, ...fifoAlloc]);
  const total = round2(merged.reduce((s, a) => s + a.amount, 0));
  if (total <= 0) throw new Error("Enter a FIFO amount and/or per-bill amounts");

  const manualSum = round2(manualAlloc.reduce((s, a) => s + a.amount, 0));
  const fifoSum = round2(fifoAlloc.reduce((s, a) => s + a.amount, 0));
  const method = manualSum > 0 && fifoSum > 0 ? "mixed" : manualSum > 0 ? "manual" : "fifo";

  return applyPayment({
    customerId,
    amount: total,
    allocations: merged,
    method,
    note,
    payMode,
    branch:
      branchFilter != null && String(branchFilter).trim() !== ""
        ? normalizeBranchValue(branchFilter)
        : null,
  });
}

/**
 * Apply payment to bills (batch). Creates creditPayments doc.
 * @param {string} [method] — allocation: fifo | manual | mixed
 * @param {string} [payMode] — instrument: cash | card_upi
 * @param {string | null} [branch] — When set, stored on the payment (outlet). Otherwise inferred from first allocated bill.
 */
export async function applyPayment({
  customerId,
  amount,
  allocations,
  method,
  note,
  payMode,
  branch: branchOverride = null,
}) {
  const pay = round2(amount);
  if (!customerId || pay <= 0) throw new Error("Customer and positive amount required");
  if (!allocations?.length) throw new Error("No allocations");

  let sumAlloc = 0;
  const clean = [];
  for (const a of allocations) {
    const amt = round2(a.amount);
    if (amt <= 0 || !a.billId) continue;
    clean.push({ billId: a.billId, amount: amt });
    sumAlloc += amt;
  }
  if (round2(sumAlloc) !== pay) throw new Error(`Allocations (${sumAlloc}) must equal payment (${pay})`);

  const batch = writeBatch(db);

  for (const { billId, amount: amt } of clean) {
    const billRef = doc(db, COLLECTION_BILLS, billId);
    const snap = await getDoc(billRef);
    if (!snap.exists()) throw new Error(`Bill ${billId} not found`);
    const data = normalizeCreditBillRow({ id: snap.id, ...snap.data() });
    if (String(data.customerId || "").trim() !== String(customerId || "").trim()) {
      throw new Error("Bill does not belong to this customer");
    }
    const total = round2(data.totalAmount);
    const paid = round2(data.paidAmount);
    const bal = round2(data.balanceAmount);
    if (amt > bal + 0.001) throw new Error(`Alloc ${amt} exceeds balance for bill ${data.voucherNo}`);
    const newPaid = round2(paid + amt);
    const newBal = round2(total - newPaid);
    const status = deriveStatus(total, newPaid, newBal);
    batch.update(billRef, {
      paidAmount: newPaid,
      balanceAmount: newBal,
      status,
      updatedAt: serverTimestamp(),
    });
  }

  const payRef = doc(collection(db, COLLECTION_PAYMENTS));
  const payModeNorm = payMode === "card_upi" ? "card_upi" : "cash";

  let paymentBranch = "Main";
  if (branchOverride != null && String(branchOverride).trim() !== "") {
    paymentBranch = normalizeBranchValue(branchOverride);
  } else if (clean.length > 0) {
    const firstBillRef = doc(db, COLLECTION_BILLS, clean[0].billId);
    const firstSnap = await getDoc(firstBillRef);
    if (firstSnap.exists()) {
      paymentBranch = creditBillBranch(normalizeCreditBillRow({ id: firstSnap.id, ...firstSnap.data() }));
    }
  }

  const paymentDateStr = localYMD(new Date());
  batch.set(payRef, {
    customerId,
    amount: pay,
    method: method || "manual",
    payMode: payModeNorm,
    allocations: clean,
    note: note || "",
    branch: paymentBranch,
    paymentDate: paymentDateStr,
    createdAt: serverTimestamp(),
  });

  await batch.commit();
  return { paymentId: payRef.id };
}

export async function applyPaymentFIFO(customerId, amount, note, payMode = "cash", branchFilter = null) {
  return applyPaymentCombined(customerId, {}, amount, note, payMode, branchFilter);
}

export function buildWhatsAppReminderMessage({ customerName, voucherNo, balanceAmount, dueDate, invoiceDate }) {
  const dueParsed = parseFirestoreOrIsoDate(dueDate);
  const invParsed = parseFirestoreOrIsoDate(invoiceDate);
  const refLabel = dueParsed ? "Due date" : "Invoice date";
  const refVal = dueParsed
    ? dueParsed.toLocaleDateString("en-IN")
    : invParsed
      ? invParsed.toLocaleDateString("en-IN")
      : "—";
  return (
    `Dear ${customerName || "Customer"},\n\n` +
    `Reminder: Bill ${voucherNo || "—"} has a pending balance of ₹${round2(balanceAmount)}.\n` +
    `${refLabel}: ${refVal}.\n\n` +
    `Please arrange payment at your earliest convenience.\n\nThank you.`
  );
}

export function whatsappUrlForPhone(phone, text) {
  const raw = String(phone || "").replace(/\D/g, "");
  if (!raw) return null;
  const last10 = raw.length > 10 ? raw.slice(-10) : raw;
  const q = encodeURIComponent(text || "");
  return `https://wa.me/91${last10}?text=${q}`;
}

export async function fetchAuditNotesForCustomer(customerId) {
  if (!customerId) return [];
  const qy = query(
    collection(db, COLLECTION_AUDIT_NOTES),
    where("customerId", "==", customerId),
    orderBy("createdAt", "desc")
  );
  try {
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    const snap = await getDocs(
      query(collection(db, COLLECTION_AUDIT_NOTES), where("customerId", "==", customerId))
    );
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => {
      const ta = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
      const tb = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
      return tb - ta;
    });
    return rows;
  }
}

export async function addAuditNoteForCustomer({
  customerId,
  customerName,
  note,
  lastContactDate,
  nextActionDate,
}) {
  const cid = String(customerId || "").trim();
  const txt = String(note || "").trim();
  if (!cid || !txt) throw new Error("Customer and note are required");
  const payload = {
    customerId: cid,
    customerName: customerName || "",
    note: txt,
    lastContactDate: String(lastContactDate || "").trim() || null,
    nextActionDate: String(nextActionDate || "").trim() || null,
    createdAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COLLECTION_AUDIT_NOTES), payload);
  return { id: ref.id, ...payload };
}

// --- Masters (full maintenance): `creditBills`, `creditPayments`, `creditCustomerAuditNotes` ---

export async function fetchAllCreditAuditNotes() {
  const snap = await getDocs(collection(db, COLLECTION_AUDIT_NOTES));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
    const tb = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
    return tb - ta;
  });
  return rows;
}

function paymentAllocatesToBillId(payment, billId) {
  const allocs = Array.isArray(payment?.allocations) ? payment.allocations : [];
  return allocs.some((a) => a?.billId === billId && round2(a?.amount) > 0);
}

/**
 * Delete a credit bill. Refuses if any payment allocates to it or paidAmount > 0.
 */
export async function masterDeleteCreditBill(billId) {
  const id = String(billId || "").trim();
  if (!id) throw new Error("Bill id required");
  const ref = doc(db, COLLECTION_BILLS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Bill not found");
  const data = normalizeCreditBillRow({ id: snap.id, ...snap.data() });
  if (round2(data.paidAmount) > 0) {
    throw new Error("Bill still has paid amount. Delete the matching receipt(s) in Payments first, or edit paid to zero.");
  }
  const payments = await fetchAllPayments();
  for (const p of payments) {
    if (paymentAllocatesToBillId(p, id)) {
      throw new Error(`Payment ${p.id} still allocates to this bill. Remove that receipt first.`);
    }
  }
  await deleteDoc(ref);
}

/**
 * Replace bill fields used in operations UI; recomputes balance and status from total and paid.
 */
export async function masterSaveCreditBill(billId, row) {
  const id = String(billId || "").trim();
  if (!id) throw new Error("Bill id required");
  const ref = doc(db, COLLECTION_BILLS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Bill not found");

  const customerId = String(row.customerId || "").trim();
  if (!customerId) throw new Error("Customer id required");

  const total = round2(row.totalAmount);
  const paid = round2(row.paidAmount);
  if (total < 0 || paid < 0) throw new Error("Amounts cannot be negative");
  if (paid > total + 0.001) throw new Error("Paid cannot exceed total");
  const balance = round2(total - paid);
  const status = deriveStatus(total, paid, balance);

  const invRaw = String(row.invoiceDate || "").trim();
  const dueRaw = String(row.dueDate || "").trim();

  const branchOut =
    row.branch !== undefined && row.branch !== null && String(row.branch).trim() !== ""
      ? normalizeBranchValue(row.branch)
      : normalizeBranchValue(normalizeCreditBillRow({ id: snap.id, ...snap.data() }).branch);

  await updateDoc(ref, {
    customerId,
    customerName: String(row.customerName || "").trim(),
    phone: String(row.phone || "").trim(),
    voucherNo: String(row.voucherNo || "").trim(),
    totalAmount: total,
    paidAmount: paid,
    balanceAmount: balance,
    status,
    invoiceDate: invRaw ? invRaw.slice(0, 10) : null,
    dueDate: dueRaw ? dueRaw.slice(0, 10) : null,
    note: String(row.note ?? ""),
    careOf: String(row.careOf || "").trim() || null,
    manual: Boolean(row.manual),
    source: String(row.source || "manual").trim() || "manual",
    invoiceId: row.invoiceId ? String(row.invoiceId).trim() : null,
    branch: branchOut,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reverse allocations on bills and delete the payment document.
 */
export async function masterDeleteCreditPayment(paymentId) {
  const pid = String(paymentId || "").trim();
  if (!pid) throw new Error("Payment id required");
  const pref = doc(db, COLLECTION_PAYMENTS, pid);
  const psnap = await getDoc(pref);
  if (!psnap.exists()) throw new Error("Payment not found");
  const p = psnap.data();
  const clean = mergeAllocationsByBillId(Array.isArray(p.allocations) ? p.allocations : []);
  if (!clean.length) {
    await deleteDoc(pref);
    return;
  }

  const batch = writeBatch(db);
  for (const { billId, amount: amt } of clean) {
    const billRef = doc(db, COLLECTION_BILLS, billId);
    const bsnap = await getDoc(billRef);
    if (!bsnap.exists()) throw new Error(`Bill ${billId} not found (orphan payment)`);
    const b = normalizeCreditBillRow({ id: bsnap.id, ...bsnap.data() });
    const total = round2(b.totalAmount);
    const paid = round2(round2(b.paidAmount) - amt);
    if (paid < -0.001) throw new Error(`Reversal would make paid negative on bill ${billId}`);
    const newBal = round2(total - paid);
    const status = deriveStatus(total, paid, newBal);
    batch.update(billRef, {
      paidAmount: paid,
      balanceAmount: newBal,
      status,
      updatedAt: serverTimestamp(),
    });
  }
  batch.delete(pref);
  await batch.commit();
}

/**
 * Replace a payment receipt's amount, allocations, and metadata in one step.
 * Applies (new − old) per bill to paid/balance; every bill must belong to `customerId`.
 *
 * @param {string} paymentId
 * @param {{
 *   customerId: string,
 *   amount?: number,
 *   allocations: { billId: string, amount: number }[],
 *   note?: string,
 *   method?: string,
 *   payMode?: string,
 *   paymentDate?: string,
 * }} payload
 */
export async function masterReplaceCreditPayment(paymentId, payload) {
  const pid = String(paymentId || "").trim();
  if (!pid) throw new Error("Payment id required");
  const pref = doc(db, COLLECTION_PAYMENTS, pid);
  const psnap = await getDoc(pref);
  if (!psnap.exists()) throw new Error("Payment not found");
  const prev = psnap.data();

  const customerId = String(payload.customerId ?? prev.customerId ?? "").trim();
  if (!customerId) throw new Error("Customer id required");

  const rawAllocs = Array.isArray(payload.allocations) ? payload.allocations : [];
  const clean = mergeAllocationsByBillId(
    rawAllocs.map((a) => ({
      billId: String(a?.billId || "").trim(),
      amount: round2(a?.amount),
    }))
  ).filter((a) => a.billId && a.amount > 0);

  if (!clean.length) throw new Error("At least one allocation with bill id and positive amount is required");

  const sumAlloc = round2(clean.reduce((s, a) => s + a.amount, 0));
  const amount =
    payload.amount !== undefined && payload.amount !== null
      ? round2(payload.amount)
      : sumAlloc;
  if (amount <= 0) throw new Error("Payment amount must be positive");
  if (Math.abs(sumAlloc - amount) > 0.001) {
    throw new Error(`Allocations sum (₹${sumAlloc}) must equal payment amount (₹${amount})`);
  }

  const oldClean = mergeAllocationsByBillId(Array.isArray(prev.allocations) ? prev.allocations : []);
  const oldMap = new Map(oldClean.map((a) => [a.billId, a.amount]));
  const newMap = new Map(clean.map((a) => [a.billId, a.amount]));
  const billIds = new Set([...oldMap.keys(), ...newMap.keys()]);

  const billRefs = new Map();
  const billData = new Map();
  for (const billId of billIds) {
    const bref = doc(db, COLLECTION_BILLS, billId);
    const bsnap = await getDoc(bref);
    if (!bsnap.exists()) throw new Error(`Bill ${billId} not found`);
    const b = normalizeCreditBillRow({ id: bsnap.id, ...bsnap.data() });
    if (String(b.customerId || "").trim() !== customerId) {
      throw new Error(
        `Bill ${billId} (customer ${b.customerId || "?"}) does not match payment customer ${customerId}.`
      );
    }
    billRefs.set(billId, bref);
    billData.set(billId, b);
  }

  const batch = writeBatch(db);
  for (const billId of billIds) {
    const oldAmt = round2(oldMap.get(billId) || 0);
    const newAmt = round2(newMap.get(billId) || 0);
    if (oldAmt === 0 && newAmt === 0) continue;
    const b = billData.get(billId);
    const total = round2(b.totalAmount);
    const paid = round2(b.paidAmount);
    const newPaid = round2(paid - oldAmt + newAmt);
    if (newPaid < -0.001) throw new Error(`Bill ${billId}: paid would become negative (check allocations)`);
    if (newPaid > total + 0.001) throw new Error(`Bill ${billId}: total paid would exceed bill total ₹${total}`);
    const newBal = round2(total - newPaid);
    const status = deriveStatus(total, newPaid, newBal);
    batch.update(billRefs.get(billId), {
      paidAmount: newPaid,
      balanceAmount: newBal,
      status,
      updatedAt: serverTimestamp(),
    });
  }

  const note = payload.note !== undefined ? String(payload.note ?? "") : prev.note || "";
  const method = payload.method !== undefined ? String(payload.method || "manual") : String(prev.method || "manual");
  const payModeNorm =
    payload.payMode !== undefined
      ? payload.payMode === "card_upi"
        ? "card_upi"
        : "cash"
      : prev.payMode === "card_upi"
        ? "card_upi"
        : "cash";

  const firstBillId = clean[0]?.billId;
  const newPaymentBranch = firstBillId ? creditBillBranch(billData.get(firstBillId)) : "Main";

  const payUpdates = {
    customerId,
    amount,
    allocations: clean,
    note,
    method: method || "manual",
    payMode: payModeNorm,
    branch: newPaymentBranch,
    updatedAt: serverTimestamp(),
  };
  if (payload.paymentDate !== undefined) {
    const t = String(payload.paymentDate ?? "").trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) payUpdates.paymentDate = t;
    else payUpdates.paymentDate = deleteField();
  }

  batch.update(pref, payUpdates);

  await batch.commit();
}

/** Metadata-only payment edits (does not re-open bill balances). */
export async function masterPatchCreditPayment(paymentId, { customerId, note, method, payMode, paymentDate }) {
  const pid = String(paymentId || "").trim();
  if (!pid) throw new Error("Payment id required");
  const ref = doc(db, COLLECTION_PAYMENTS, pid);
  const updates = { updatedAt: serverTimestamp() };
  if (customerId !== undefined) updates.customerId = String(customerId || "").trim();
  if (note !== undefined) updates.note = String(note ?? "");
  if (method !== undefined) updates.method = String(method || "manual");
  if (payMode !== undefined) updates.payMode = payMode === "card_upi" ? "card_upi" : "cash";
  if (paymentDate !== undefined) {
    const t = String(paymentDate ?? "").trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) updates.paymentDate = t;
    else updates.paymentDate = deleteField();
  }
  await updateDoc(ref, updates);
}

export async function masterUpdateCreditAuditNote(noteId, patch) {
  const id = String(noteId || "").trim();
  if (!id) throw new Error("Note id required");
  const ref = doc(db, COLLECTION_AUDIT_NOTES, id);
  const updates = { updatedAt: serverTimestamp() };
  if (patch.customerId !== undefined) updates.customerId = String(patch.customerId || "").trim();
  if (patch.customerName !== undefined) updates.customerName = String(patch.customerName || "").trim();
  if (patch.note !== undefined) {
    const t = String(patch.note || "").trim();
    if (!t) throw new Error("Note text required");
    updates.note = t;
  }
  if (patch.lastContactDate !== undefined) {
    updates.lastContactDate = String(patch.lastContactDate || "").trim() || null;
  }
  if (patch.nextActionDate !== undefined) {
    updates.nextActionDate = String(patch.nextActionDate || "").trim() || null;
  }
  await updateDoc(ref, updates);
}

export async function masterDeleteCreditAuditNote(noteId) {
  const id = String(noteId || "").trim();
  if (!id) throw new Error("Note id required");
  await deleteDoc(doc(db, COLLECTION_AUDIT_NOTES, id));
}
